import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import AdminPageShell from '../AdminPageShell';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { fetchAdminAuditActionDetail } from '../../../services/academiaAdminService';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import {
  AcademicPanelCard,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';

type BackupFileRow = {
  nom?: string;
  chemin?: string;
  tailleOctets?: number | null;
  tailleLisible?: string | null;
  dateModification?: string | null;
  existe?: boolean;
};

type AuditDetailData = {
  id?: string;
  when?: string;
  action?: string;
  danger?: boolean;
  acteur?: {
    id?: string | null;
    nom?: string;
    telephone?: string | null;
    email?: string | null;
    role?: string | null;
  };
  detailsComplets?: string | null;
  detailsStructure?: unknown;
  sauvegarde?: {
    declenchement?: string | null;
    copiesExpireesSupprimees?: number | null;
    fichier?: BackupFileRow | null;
    politique?: {
      actif?: boolean;
      frequence?: string;
      conservationJours?: number;
      emplacement?: string;
    };
    etatGlobal?: {
      copiesDisponibles?: number;
      derniereReussite?: string | null;
      derniereErreur?: string | null;
      dernierFichier?: string | null;
    };
    inventaire?: BackupFileRow[];
    meta?: Record<string, unknown> | null;
    donneesEnregistrees?: Record<string, unknown> | null;
  } | null;
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR');
  } catch {
    return '—';
  }
}

function formatBackupSchedule(cron?: string) {
  if (!cron?.trim()) return '—';
  const parts = cron.trim().split(/\s+/);
  if (parts.length >= 2 && parts[0] !== '*' && parts[1] !== '*' && parts[2] === '*') {
    return `Tous les jours à ${parts[1].padStart(2, '0')}:${parts[0].padStart(2, '0')}`;
  }
  return cron;
}

function DetailGrid({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">{row.label}</dt>
          <dd className="mt-1 break-words text-sm font-medium text-slate-900">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  if (value == null) return <p className="text-sm text-on-surface-variant">—</p>;
  return (
    <pre className="max-h-80 overflow-auto rounded-lg border border-outline-variant/20 bg-surface-container-low p-3 text-xs text-slate-800">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export default function AdminAuditActionDetailPanel() {
  const { auditId } = useParams<{ auditId: string }>();
  const [data, setData] = useState<AuditDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchAdminAuditActionDetail(auditId);
      setData(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le détail de cette action.');
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const sauvegarde = data?.sauvegarde;
  const isBackup = Boolean(sauvegarde);

  return (
    <AdminPageShell
      title="Détail de l'action"
      description="Consultation complète d'une opération enregistrée dans le suivi administratif."
      sideCard={{
        label: 'Retour',
        value: 'Liste',
        helper: 'Suivi des actions administratives',
        sideLinks: [{ label: 'Sécurité', to: '/admin/security', icon: 'shield' }],
      }}
      isLoading={loading && !data}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement du détail…"
    >
      {data ? (
        <div className="space-y-6">
          <div>
            <Link
              to="/admin/security"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <LucideSymbolIcon name="arrow_back" tone="inherit" size={16} />
              Retour au suivi des actions
            </Link>
          </div>

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="history" tone="primary" size={22} />}
            title={data.action ?? 'Action'}
            subtitle={`Enregistrée le ${formatDate(data.when)}`}
            badge={
              <span
                className={
                  data.danger
                    ? 'academic-panel-badge border-amber-200 bg-amber-50 text-amber-900'
                    : 'academic-panel-badge academic-panel-badge--success'
                }
              >
                {data.danger ? 'Alerte' : 'Normal'}
              </span>
            }
          >
            <DetailGrid
              rows={[
                { label: 'Date et heure', value: formatDate(data.when) },
                { label: 'Opération', value: data.action ?? '—' },
                { label: 'Réalisée par', value: data.acteur?.nom ?? '—' },
                { label: 'Profil', value: data.acteur?.role ?? '—' },
                { label: 'Téléphone', value: data.acteur?.telephone ?? '—' },
                { label: 'E-mail', value: data.acteur?.email ?? '—' },
              ]}
            />
          </AcademicPanelCard>

          {!isBackup && data.detailsComplets ? (
            <AcademicPanelCard
              icon={<LucideSymbolIcon name="description" tone="primary" size={22} />}
              title="Informations enregistrées"
              subtitle="Contenu complet associé à cette action"
            >
              {data.detailsStructure ? (
                <JsonBlock value={data.detailsStructure} />
              ) : (
                <p className="whitespace-pre-wrap break-words text-sm text-slate-800">{data.detailsComplets}</p>
              )}
            </AcademicPanelCard>
          ) : null}

          {isBackup && sauvegarde ? (
            <>
              <AcademicPanelCard
                icon={<LucideSymbolIcon name="save" tone="primary" size={22} />}
                title="Résumé de la sauvegarde"
                subtitle="Informations principales de l'opération"
              >
                <DetailGrid
                  rows={[
                    {
                      label: 'Déclenchement',
                      value:
                        sauvegarde.declenchement === 'automatique'
                          ? 'Automatique (planifiée)'
                          : sauvegarde.declenchement === 'manuel'
                            ? 'Manuel (administrateur)'
                            : '—',
                    },
                    {
                      label: 'Copies expirées supprimées',
                      value:
                        sauvegarde.copiesExpireesSupprimees != null
                          ? String(sauvegarde.copiesExpireesSupprimees)
                          : '—',
                    },
                    {
                      label: 'Sauvegardes automatiques',
                      value: sauvegarde.politique?.actif ? 'Activées' : 'Désactivées',
                    },
                    {
                      label: 'Fréquence prévue',
                      value: formatBackupSchedule(sauvegarde.politique?.frequence),
                    },
                    {
                      label: 'Durée de conservation',
                      value: `${sauvegarde.politique?.conservationJours ?? '—'} jours`,
                    },
                    {
                      label: 'Emplacement de stockage',
                      value: sauvegarde.politique?.emplacement ?? '—',
                    },
                  ]}
                />
              </AcademicPanelCard>

              <AcademicPanelCard
                icon={<LucideSymbolIcon name="inventory_2" tone="primary" size={22} />}
                title="Fichier de sauvegarde concerné"
                subtitle="Détails complets du fichier produit ou visé par cette action"
              >
                <DetailGrid
                  rows={[
                    { label: 'Nom du fichier', value: sauvegarde.fichier?.nom ?? '—' },
                    { label: 'Chemin complet', value: sauvegarde.fichier?.chemin ?? '—' },
                    {
                      label: 'Taille',
                      value:
                        sauvegarde.fichier?.tailleLisible ??
                        (sauvegarde.fichier?.tailleOctets != null
                          ? `${sauvegarde.fichier.tailleOctets} octets`
                          : '—'),
                    },
                    {
                      label: 'Taille (octets)',
                      value:
                        sauvegarde.fichier?.tailleOctets != null
                          ? String(sauvegarde.fichier.tailleOctets)
                          : '—',
                    },
                    {
                      label: 'Date du fichier',
                      value: formatDate(sauvegarde.fichier?.dateModification),
                    },
                    {
                      label: 'Fichier présent sur le serveur',
                      value: sauvegarde.fichier?.existe ? 'Oui' : 'Non',
                    },
                  ]}
                />
              </AcademicPanelCard>

              <AcademicPanelCard
                icon={<LucideSymbolIcon name="insights" tone="primary" size={22} />}
                title="État global du service de sauvegarde"
                subtitle="Situation actuelle du système de copies"
              >
                <DetailGrid
                  rows={[
                    {
                      label: 'Copies disponibles',
                      value: String(sauvegarde.etatGlobal?.copiesDisponibles ?? '—'),
                    },
                    {
                      label: 'Dernière sauvegarde réussie',
                      value: formatDate(sauvegarde.etatGlobal?.derniereReussite),
                    },
                    {
                      label: 'Dernier fichier créé',
                      value: sauvegarde.etatGlobal?.dernierFichier ?? '—',
                    },
                    {
                      label: 'Dernière erreur enregistrée',
                      value: sauvegarde.etatGlobal?.derniereErreur ?? 'Aucune',
                    },
                  ]}
                />
              </AcademicPanelCard>

              <AcademicPanelCard
                flush
                icon={<LucideSymbolIcon name="folder_open" tone="primary" size={22} />}
                title="Inventaire complet des copies"
                subtitle="Tous les fichiers de sauvegarde actuellement conservés"
              >
                <AcademicPanelTableWrap
                  empty={
                    (sauvegarde.inventaire ?? []).length === 0 ? (
                      <p className="text-sm text-on-surface-variant">Aucun fichier de sauvegarde trouvé.</p>
                    ) : undefined
                  }
                >
                  {(sauvegarde.inventaire ?? []).length > 0 ? (
                    <table className="academic-panel-table">
                      <thead>
                        <tr>
                          <th>Fichier</th>
                          <th>Taille</th>
                          <th>Octets</th>
                          <th>Date</th>
                          <th>Présent</th>
                          <th className="hidden lg:table-cell">Chemin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sauvegarde.inventaire ?? []).map((file) => (
                          <tr key={`${file.nom}-${file.chemin}`}>
                            <td className="font-medium text-slate-900">{file.nom ?? '—'}</td>
                            <td>{file.tailleLisible ?? '—'}</td>
                            <td>{file.tailleOctets ?? '—'}</td>
                            <td className="whitespace-nowrap text-xs">{formatDate(file.dateModification)}</td>
                            <td>{file.existe ? 'Oui' : 'Non'}</td>
                            <td className="hidden max-w-xs truncate lg:table-cell text-xs text-on-surface-variant">
                              {file.chemin ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : null}
                </AcademicPanelTableWrap>
              </AcademicPanelCard>

              {sauvegarde.meta ? (
                <AcademicPanelCard
                  icon={<LucideSymbolIcon name="data_object" tone="primary" size={22} />}
                  title="Métadonnées du service"
                  subtitle="Informations techniques enregistrées par le système de sauvegarde"
                >
                  <JsonBlock value={sauvegarde.meta} />
                </AcademicPanelCard>
              ) : null}

              {sauvegarde.donneesEnregistrees ? (
                <AcademicPanelCard
                  icon={<LucideSymbolIcon name="article" tone="primary" size={22} />}
                  title="Données enregistrées lors de l'action"
                  subtitle="Contenu complet sauvegardé dans le journal au moment de l'opération"
                >
                  <JsonBlock value={sauvegarde.donneesEnregistrees} />
                </AcademicPanelCard>
              ) : data.detailsComplets ? (
                <AcademicPanelCard
                  icon={<LucideSymbolIcon name="article" tone="primary" size={22} />}
                  title="Données enregistrées lors de l'action"
                  subtitle="Contenu complet sauvegardé dans le journal au moment de l'opération"
                >
                  <p className="whitespace-pre-wrap break-words text-sm text-slate-800">{data.detailsComplets}</p>
                </AcademicPanelCard>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </AdminPageShell>
  );
}
